import React, { useId } from 'react';

export interface SegmentedOption<T extends string> {
  value: T;
  label: string;
  /** Optional tooltip / longer description. */
  title?: string;
}

interface SegmentedRadioGroupProps<T extends string> {
  /** Accessible group name (rendered as the fieldset legend; visually hidden unless `showLegend`). */
  legend: string;
  showLegend?: boolean;
  options: ReadonlyArray<SegmentedOption<T>>;
  value: T;
  onChange: (value: T) => void;
  /** The radios' shared `name`; defaults to a unique id. */
  name?: string;
}

/**
 * A single-choice selector drawn as the app's segmented control, built on NATIVE radio buttons inside a
 * fieldset. That gets the platform behaviour for free: one tab stop for the group, arrow keys move the
 * selection, and screen readers announce "radio button, 1 of 2, selected". The look matches the existing
 * inline segmented controls (CRNA "By site / By group", the roster's Day / Week / Month) and uses theme
 * tokens only, so it works in light and dark mode. Styles live in App.css under `.segmented-radio`.
 */
function SegmentedRadioGroup<T extends string>({ legend, showLegend = false, options, value, onChange, name }: SegmentedRadioGroupProps<T>) {
  const autoName = useId();
  const groupName = name ?? `segmented-${autoName}`;
  return (
    <fieldset className="segmented-radio">
      <legend className={showLegend ? 'segmented-radio-legend' : 'visually-hidden'}>{legend}</legend>
      <div className="segmented-radio-track">
        {options.map(opt => (
          <label key={opt.value} className="segmented-radio-option" title={opt.title}>
            <input
              type="radio"
              className="visually-hidden"
              name={groupName}
              value={opt.value}
              checked={value === opt.value}
              onChange={() => onChange(opt.value)}
            />
            <span>{opt.label}</span>
          </label>
        ))}
      </div>
    </fieldset>
  );
}

export default SegmentedRadioGroup;
